/**
 * Admin team members + invite endpoints.
 *
 * Member management:
 *   listMembers, inviteMember, getAccess, updateAccess, changeRole, promoteMember,
 *   suspendMember, resumeMember, removeMember
 *
 * Invite management:
 *   listInvites, resendInvite, revokeInvite
 *
 * Public (no auth):
 *   verifyInvite, acceptInvite
 */
import { apiClient } from '@discovr/common';
import type { PaginatedResponse, DataResponse } from '@discovr/common';
import type { GrantLevel } from './campaignGrants';

/** A campaign-grant to apply as part of an invite (before the invitee exists as a member). */
export interface PendingGrant {
  campaign_id: string;
  level: GrantLevel;
}

/** A single campaign's access grant, hydrated with campaign + brand name for display. */
export interface MemberCampaignGrant {
  campaign_id: string;
  campaign_name: string;
  brand_name?: string;
  level: GrantLevel;
  granted_at: string;
}

/** A member's full campaign access — `all_campaigns: true` covers super-admin
 *  unrestricted access, so `grants` is only meaningful (and only sent by the
 *  backend) when it's false. Discriminated on `all_campaigns` so a caller can't
 *  represent (or need to handle) `all_campaigns: true` with a non-empty `grants`. */
export type MemberAccess =
  | { all_campaigns: true }
  | { all_campaigns: false; grants: MemberCampaignGrant[] };

/** Roles assignable via `changeRole` — 'super_admin' is never a target of a
 *  plain role change. */
export type AdminTeamRole = 'admin' | 'admin_member';

export interface AdminTeamMember {
  id: string;
  user_id: string;
  email: string;
  display_name?: string;
  role: 'super_admin' | 'admin' | 'admin_member';
  status: 'active' | 'suspended' | 'inactive';
  accepted_at: string;
  created_at: string;
}

export interface AdminTeamInvite {
  id: string;
  email: string;
  role: 'admin_member';
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  created_at: string;
  expires_at: string;
}

export interface VerifyAdminInviteResponse {
  email: string;
  role: string;
  [key: string]: unknown;
}

export interface AcceptAdminInviteResponse {
  firebase_custom_token: string;
  user: unknown;
}

export const adminTeam = {
  // ─── Public (no auth) ─────────────────────────────────────────────────────

  /** Verify an admin invite token (public, from email link). */
  verifyInvite: (token: string): Promise<VerifyAdminInviteResponse> => {
    const params = new URLSearchParams({ token });
    return apiClient
      .get<DataResponse<VerifyAdminInviteResponse>>(`/admin-invites/verify?${params}`)
      .then((r) => r.data.data);
  },

  /** Accept an admin invite and set password (public, from email link). */
  acceptInvite: (body: {
    token: string;
    password: string;
    first_name: string;
    last_name: string;
  }): Promise<AcceptAdminInviteResponse> =>
    apiClient
      .post<DataResponse<AcceptAdminInviteResponse>>('/admin-invites/accept', body)
      .then((r) => r.data.data),

  /** Accept an admin invite via a Google sign-in the FE already completed client-side (public). */
  acceptInviteGoogle: (body: {
    token: string;
    id_token: string;
  }): Promise<AcceptAdminInviteResponse> =>
    apiClient
      .post<DataResponse<AcceptAdminInviteResponse>>('/admin-invites/accept-google', body)
      .then((r) => r.data.data),

  // ─── Authenticated ─────────────────────────────────────────────────────────

  /** List admin team members (paginated). */
  listMembers: (params?: { page?: number; limit?: number }): Promise<PaginatedResponse<AdminTeamMember>> =>
    apiClient
      .get<PaginatedResponse<AdminTeamMember>>('/team', { params })
      .then((r) => r.data),

  /** Invite a new admin team member, optionally granting campaign access up front. */
  inviteMember: (body: { email: string; role: string; campaign_grants?: PendingGrant[] }): Promise<AdminTeamInvite> =>
    apiClient
      .post<DataResponse<AdminTeamInvite>>('/team', body)
      .then((r) => r.data.data),

  /** Get a team member's campaign access grants. */
  getAccess: (id: string): Promise<MemberAccess> =>
    apiClient
      .get<DataResponse<MemberAccess>>(`/team/${encodeURIComponent(id)}/access`)
      .then((r) => r.data.data),

  /** Upsert and/or revoke a team member's campaign access grants in one call. */
  updateAccess: (id: string, body: { upsert?: PendingGrant[]; revoke?: string[] }): Promise<MemberAccess> =>
    apiClient
      .put<DataResponse<MemberAccess>>(`/team/${encodeURIComponent(id)}/access`, body)
      .then((r) => r.data.data),

  /** Change a team member's role. WriteNoContent → 204. */
  changeRole: (id: string, role: AdminTeamRole): Promise<void> =>
    apiClient
      .post(`/team/${encodeURIComponent(id)}/role`, { role })
      .then(() => undefined),

  /**
   * Promote a Staff member to Admin. Super Admin only — the backend
   * rejects with 403 for any other caller, and never accepts a target
   * that isn't currently active Staff. WriteNoContent → 204.
   */
  promoteMember: (id: string): Promise<void> =>
    apiClient
      .post(`/team/${encodeURIComponent(id)}/promote`)
      .then(() => undefined),

  /**
   * Suspend an admin team member. WriteNoContent → 204.
   * `body.confirm` carries the typed-confirmation value (ActionConfirmDialog's
   * `confirmText="CONFIRM"` gate) for the BE's confirm.Matches check.
   */
  suspendMember: (id: string, body?: { confirm?: string }): Promise<void> =>
    apiClient
      .post(`/team/${encodeURIComponent(id)}/suspend`, body)
      .then(() => undefined),

  /**
   * Resume a suspended admin team member. WriteNoContent → 204.
   * `body.confirm` — see `suspendMember`.
   */
  resumeMember: (id: string, body?: { confirm?: string }): Promise<void> =>
    apiClient
      .post(`/team/${encodeURIComponent(id)}/resume`, body)
      .then(() => undefined),

  /**
   * Remove an admin team member. WriteNoContent → 204.
   * `body.confirm` carries the typed-confirmation value (ActionConfirmDialog's
   * `confirmText={member.email}` gate) for the BE's confirm.Matches check. DELETE
   * has no body param on axios's shorthand, so it goes through `config.data`.
   */
  removeMember: (id: string, body?: { confirm?: string }): Promise<void> =>
    apiClient
      .delete(`/team/${encodeURIComponent(id)}`, { data: body })
      .then(() => undefined),

  /** Send a password-reset link to an admin team member. WriteNoContent → 204. */
  resetMemberPassword: (id: string): Promise<void> =>
    apiClient
      .post(`/team/${encodeURIComponent(id)}/reset-password`)
      .then(() => undefined),

  /** List admin team invites (paginated). */
  listInvites: (params?: { page?: number; limit?: number }): Promise<PaginatedResponse<AdminTeamInvite>> =>
    apiClient
      .get<PaginatedResponse<AdminTeamInvite>>('/team/invites', { params })
      .then((r) => r.data),

  /** Resend a pending admin invite. WriteNoContent → 204. */
  resendInvite: (id: string): Promise<void> =>
    apiClient
      .post(`/team/invites/${encodeURIComponent(id)}/resend`)
      .then(() => undefined),

  /**
   * Revoke a pending admin invite. WriteNoContent → 204.
   * `body.confirm` — see `suspendMember`. DELETE has no body param on
   * axios's shorthand, so it goes through `config.data`.
   */
  revokeInvite: (id: string, body?: { confirm?: string }): Promise<void> =>
    apiClient
      .delete(`/team/invites/${encodeURIComponent(id)}`, { data: body })
      .then(() => undefined),
};
